import React, { useEffect, useState } from "react";

import { KnowledgeAttachmentPicker } from "./KnowledgeAttachmentPicker";
import type { KnowledgeItemData } from "../lib/api";
import type { CompanyResearchIntentType } from "../lib/companyResearch";
import { getKnowledgeItemDisplayName } from "../lib/knowledge";

const COMPANY_RESEARCH_URL_KNOWLEDGE_TYPES = ["pdf", "executive_memo", "url"] as const;
const URL_SCHEME_PATTERN = /^[A-Za-z][A-Za-z0-9+.-]*:\/\//;

export function normalizeOptionalSelfCompanyUrl(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  return URL_SCHEME_PATTERN.test(trimmed) ? trimmed : `https://${trimmed}`;
}

const INTENT_CONTENT: Record<CompanyResearchIntentType, { title: string; description: string; submitLabel: string }> = {
  learn_more: {
    title: "詳しく調べる",
    description: "公開情報をさらに収集し、企業の事業・動向・特徴を詳しく整理します。",
    submitLabel: "詳しく調べる",
  },
  sales: {
    title: "売り込みたい",
    description: "相手企業の情報と任意の自社情報をもとに、営業提案の切り口を整理します。",
    submitLabel: "この内容で分析",
  },
  partnership: {
    title: "協業したい",
    description: "相手企業の情報と任意の自社情報をもとに、協業の接点やテーマを整理します。",
    submitLabel: "この内容で分析",
  },
  risk_check: {
    title: "リスクを確認したい",
    description: "公開情報をもとに、提案や取引の前に確認したい懸念点を整理します。",
    submitLabel: "分析を開始",
  },
};

export function shouldPromptForSelfCompanyUrl(intentType: CompanyResearchIntentType) {
  return intentType === "sales" || intentType === "partnership";
}

export function supportsUrlKnowledgeForCompanyResearch(intentType: CompanyResearchIntentType | null): boolean {
  return intentType === "sales" || intentType === "partnership";
}

function projectCompanyResearchKnowledgeSelection(item: KnowledgeItemData | null): KnowledgeItemData | null {
  if (!item) return null;

  return {
    id: item.id,
    type: item.type,
    title: getKnowledgeItemDisplayName(item),
    status: item.status,
    original_filename: null,
    mime_type: null,
    size_bytes: null,
    page_count: item.page_count,
    text_extractable: item.text_extractable,
    ai_processing_allowed: item.ai_processing_allowed,
    ai_preparation_status: item.ai_preparation_status ?? null,
    ai_preparation_failure_code: null,
    can_retry_ai_preparation: false,
    failure_reason: null,
    failure_code: null,
    failure_stage: null,
    failure_message: null,
    can_retry: false,
    summary: null,
    has_summary: item.has_summary,
    summarized_at: item.summarized_at ?? null,
    domain: item.type === "url" ? item.domain ?? null : null,
    created_at: null,
    updated_at: null,
  };
}

type CompanyResearchSelfUrlModalProps = {
  intentType: CompanyResearchIntentType | null;
  hasDetailedResearch?: boolean;
  isDetailedResearchRunning?: boolean;
  isReanalysis?: boolean;
  onRequestLearnMore?: () => void;
  onClose: () => void;
  onSubmit: (selfCompanyUrl: string | null, selfCompanyKnowledgeItemId: string | null) => void | Promise<void>;
  isSubmitting?: boolean;
  errorMessage?: string | null;
};

export function CompanyResearchSelfUrlModal({
  intentType,
  hasDetailedResearch = false,
  isDetailedResearchRunning = false,
  isReanalysis = false,
  onRequestLearnMore,
  onClose,
  onSubmit,
  isSubmitting = false,
  errorMessage = null,
}: CompanyResearchSelfUrlModalProps) {
  const [selfCompanyUrl, setSelfCompanyUrl] = useState("");
  const [selectedKnowledgeItem, setSelectedKnowledgeItem] = useState<KnowledgeItemData | null>(null);
  const [continueWithoutDetailedResearch, setContinueWithoutDetailedResearch] = useState(false);

  useEffect(() => {
    setSelfCompanyUrl("");
    setSelectedKnowledgeItem(null);
    setContinueWithoutDetailedResearch(false);
  }, [intentType]);

  if (!intentType) return null;

  const content = INTENT_CONTENT[intentType];
  const usesSelfCompanyInfo = shouldPromptForSelfCompanyUrl(intentType);
  const isDownstreamIntent = intentType !== "learn_more";
  const needsDetailedResearchChoice = isDownstreamIntent
    && !hasDetailedResearch
    && !continueWithoutDetailedResearch;
  const allowsUrlKnowledge = supportsUrlKnowledgeForCompanyResearch(intentType);
  const knowledgeTypeDescription = allowsUrlKnowledge
    ? "PDF、Webページ、または社長メモから"
    : "PDFまたは社長メモから";
  const knowledgeDescription = intentType === "partnership"
    ? `${knowledgeTypeDescription}自社の事業や強みが分かる資料を1件選ぶと、相手企業との協業可能性を考える際に利用します。`
    : `${knowledgeTypeDescription}自社サービスや実績が分かる資料を1件選ぶと、相手企業に合う提案の切り口を考える際に利用します。`;

  const submitWithSelfInfo = () => {
    void onSubmit(normalizeOptionalSelfCompanyUrl(selfCompanyUrl), selectedKnowledgeItem?.id ?? null);
  };

  const submitWithoutSelfInfo = () => {
    void onSubmit(null, null);
  };

  const close = () => {
    setSelfCompanyUrl("");
    setSelectedKnowledgeItem(null);
    setContinueWithoutDetailedResearch(false);
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="company-research-preflight-title"
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/35 px-3 py-4 sm:px-4 sm:py-6"
    >
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-[#EADBC8] bg-white shadow-[0_24px_60px_rgba(74,62,57,0.24)]">
        <div className="flex items-start justify-between gap-4 border-b border-[#F2E6DA] px-4 py-4 sm:px-5">
          <div className="min-w-0">
            <h2 id="company-research-preflight-title" className="break-words text-base font-black text-[#5D4D46]">
              {content.title}
            </h2>
            <p className="mt-2 text-sm font-bold leading-6 text-[#6B5851]">{content.description}</p>
            <p className="mt-3 rounded-xl bg-[#FFF7F2] px-3 py-2 text-sm font-black text-[#7C5A4E]">
              「企業リサーチ」を1回使用します
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            disabled={isSubmitting}
            className="shrink-0 rounded-full border border-[#EADBC8] bg-white px-3 py-1 text-xs font-black text-[#8A746A] hover:bg-[#FFF7F2] disabled:cursor-not-allowed disabled:opacity-60"
          >
            閉じる
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5">
          {isReanalysis ? (
            <div className="rounded-2xl border border-[#F0D0B8] bg-[#FFF8F2] p-4 text-sm font-bold leading-6 text-[#7C5A4E]">
              すでに{intentType === "learn_more" ? "調査" : "分析"}済みです。
              {intentType === "learn_more" ? "再調査" : "再分析"}すると「企業リサーチ」をもう1回使用し、新しい結果を作成します。
            </div>
          ) : null}

          {isDownstreamIntent && hasDetailedResearch ? (
            <p className="rounded-2xl bg-[#F4FAF1] p-4 text-sm font-black text-[#4D6A48]">
              ✓ 詳細調査済みの企業情報を自動利用します
            </p>
          ) : null}

          {needsDetailedResearchChoice ? (
            <div className="rounded-2xl border border-[#EADBC8] bg-[#FFFCFA] p-4">
              <p className="text-sm font-bold leading-6 text-[#6B5851]">
                {isDetailedResearchRunning
                  ? "「詳しく調べる」を実行中です。完了すると、詳細調査済みの情報を目的別分析に利用できます。"
                  : "より詳しい企業情報をもとに分析するには、先に「詳しく調べる」を実行するのがおすすめです。"}
              </p>
              <div className={`mt-4 grid gap-3 ${isDetailedResearchRunning ? "" : "sm:grid-cols-2"}`}>
                {!isDetailedResearchRunning ? (
                  <button
                    type="button"
                    onClick={onRequestLearnMore}
                    disabled={isSubmitting}
                    className="min-h-[44px] rounded-full bg-[#7C5A4E] px-4 py-2 text-sm font-black text-white transition hover:bg-[#5D4D46] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    先に詳しく調べる（おすすめ）
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => setContinueWithoutDetailedResearch(true)}
                  disabled={isSubmitting}
                  className="min-h-[44px] rounded-full border border-[#D8C0AF] bg-white px-4 py-2 text-sm font-black text-[#7C5A4E] transition hover:border-[#A67C52] hover:bg-[#FFF7F2] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  このまま分析
                </button>
              </div>
            </div>
          ) : usesSelfCompanyInfo ? (
            <div>
              <h3 className="text-sm font-black text-[#5D4D46]">今回の分析で使う情報</h3>
              <p className="mt-2 text-xs font-bold leading-5 text-[#8A746A]">相手企業：企業情報の確認</p>
              <label className="mt-4 block">
                <span className="text-xs font-black text-[#5D4D46]">自社URL 任意</span>
                <input
                  type="text"
                  inputMode="url"
                  value={selfCompanyUrl}
                  onChange={(event) => setSelfCompanyUrl(event.target.value)}
                  disabled={isSubmitting}
                  className="mt-2 w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm text-[#4A3E39] outline-none transition focus:border-[#A67C52] focus:ring-2 focus:ring-[#F1D8C6] disabled:cursor-not-allowed disabled:opacity-70"
                  placeholder="https://example.co.jp/"
                />
              </label>

              <KnowledgeAttachmentPicker
                value={selectedKnowledgeItem}
                onChange={(item) => setSelectedKnowledgeItem(projectCompanyResearchKnowledgeSelection(item))}
                mode="companyResearch"
                allowedTypes={allowsUrlKnowledge ? COMPANY_RESEARCH_URL_KNOWLEDGE_TYPES : undefined}
                showUpload={false}
                allowDelete={false}
                compact
                heading="保存済みナレッジを使う（任意）"
                description={knowledgeDescription}
                usageNote={null}
                emptyMessage="ナレッジは未選択です。自社URLのみ、または自社情報なしでも分析できます。"
                selectedLabel="選択中のナレッジ"
                detachLabel="選択解除"
                pollingMessage="ナレッジを読み取り中です。完了後に選択できます。"
                unavailableFallbackMessage="このナレッジはまだ利用できません。別のナレッジを選ぶか、選択せずに進めてください。"
              />
            </div>
          ) : (
            <p className="text-sm font-bold leading-6 text-[#6B5851]">
              内容を確認し、開始ボタンを押すまで分析は実行されません。
            </p>
          )}

          {errorMessage ? (
            <p role="alert" className="mt-4 rounded-2xl bg-[#FFF0EB] p-3 text-sm font-bold text-[#B4533A]">
              {errorMessage}
            </p>
          ) : null}
        </div>

        {!needsDetailedResearchChoice ? (
          <div className={`grid gap-3 border-t border-[#F2E6DA] bg-white px-4 py-4 sm:px-5 ${usesSelfCompanyInfo ? "sm:grid-cols-2" : "sm:grid-cols-[1fr_auto]"}`}>
            <button
              type="button"
              onClick={usesSelfCompanyInfo ? submitWithSelfInfo : submitWithoutSelfInfo}
              disabled={isSubmitting}
              className="min-h-[44px] rounded-full bg-[#7C5A4E] px-4 py-2 text-sm font-black text-white transition hover:bg-[#5D4D46] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting
                ? "開始中..."
                : isReanalysis
                  ? intentType === "learn_more" ? "再調査する" : "再分析する"
                  : content.submitLabel}
            </button>
            {usesSelfCompanyInfo ? (
              <button
                type="button"
                onClick={submitWithoutSelfInfo}
                disabled={isSubmitting}
                className="min-h-[44px] rounded-full border border-[#D8C0AF] bg-white px-4 py-2 text-sm font-black text-[#7C5A4E] transition hover:border-[#A67C52] hover:bg-[#FFF7F2] disabled:cursor-not-allowed disabled:opacity-60"
              >
                自社情報なしで分析
              </button>
            ) : (
              <button
                type="button"
                onClick={close}
                disabled={isSubmitting}
                className="min-h-[44px] rounded-full border border-[#D8C0AF] bg-white px-4 py-2 text-sm font-black text-[#7C5A4E] transition hover:border-[#A67C52] hover:bg-[#FFF7F2] disabled:cursor-not-allowed disabled:opacity-60"
              >
                キャンセル
              </button>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
